/**
 * @author omriha
 * 05/02/2020
 */
import React from 'react';
import {connect} from 'react-redux';
import {push} from 'connected-react-router';
import {useTranslation} from 'react-i18next';
import queryString from 'query-string';
import SMCPage from '../../../components/core/pageComponents/SMCPage';
import ButtonContainer from '../../../components/Simple/ButtonContainer';
import {useEditorDimensions} from '../../../utils/hooks';
import LabelContainer from '../../../components/Page/LabelContainer';
import SMCEditor from '../../../components/Editor';
import SMCButton from '../../../components/Simple/SMCButton';
import {useImageEditor} from './ImageEditorHelper';
import {customImagePermissionsSelector} from '../../../store/selectors/user.selectors';
import {SMCDisabledProvider} from '../../../components/Simple/SMCDisabledContext';
import RouteLeavingGuard from '../../PopupPages/RouteLeavingGuard';
import VerifyButtonsContainer from "../VerifyButtonsContainer";
import ButtonsContainerStyles from "../ButtonsContainerStyles";


/**
 * High level element to fetch, edit and save image, all logic implemented inside.
 * @param {string} id Id of image to fetch from the server to edit
 */
function ImageEditor({
    navigateTo,
    id,
    urlParams,
    customImagePermissions
}) {
    const { t } = useTranslation();
    const { editorWidth, editorHeight } = useEditorDimensions();

    const modifyPermission = customImagePermissions ? customImagePermissions.modify : true;
    const deletePermission = customImagePermissions ? customImagePermissions.delete : true;
    const classes = ButtonsContainerStyles();

    let defaultPage = 'general';
    const queryParams = queryString.parse(urlParams);
    if (queryParams && queryParams['page']) {
        defaultPage = queryParams['page'];
    }

    const {
        imageObject,
        isObjectDirty,
        editorPages,
        helpId,
        isPageDataLoaded,
        isPageLoadFailed,
        isInnerPageLoading,
        chosenPageId,
        errorMessagesByPageList,
        onPageChange,
        setImageData,
        saveButtonOnClick,
        deleteButtonOnClick,
        revertButtonOnClick,
        reloadPageData
    } = useImageEditor(id, defaultPage);
    
    // Data passed down to the editor pages
    const editorPageData = {
        imageObject,
        setEditorPageId: onPageChange,
        setImageData,
        isPageDataLoaded,
    }

    return (
        <>
        <RouteLeavingGuard when={isObjectDirty} />
        <SMCPage loading={!isPageDataLoaded} helpId={helpId}>
            <div className="ObjectEditor" style={{ width: editorWidth }}>
                <ButtonContainer className={classes.headerContainer}>
                    <LabelContainer value={id}/>
                    <div>
                        <div style={{display: 'flex'}}>
                            <ButtonContainer>
                                <SMCButton
                                    labelClassName={classes.labelColor}
                                    icon="delete"
                                    variant="ghost"
                                    disabled={!isPageDataLoaded || !deletePermission}
                                    onClick={deleteButtonOnClick}>
                                    {t('SMP_DELETE_IMAGE')}
                                </SMCButton>
                            </ButtonContainer>
                        </div>
                    </div>
                </ButtonContainer>
                <SMCDisabledProvider forceDisable={!modifyPermission}>
                    <SMCEditor
                        editorPageData={editorPageData}
                        height={editorHeight}
                        width={editorWidth}
                        errorListByPage={errorMessagesByPageList}
                        chosenPageId={chosenPageId}
                        onPageChange={onPageChange}
                        isPageLoaded={isPageDataLoaded && !isInnerPageLoading}
                        isPageLoadFailed={isPageLoadFailed}
                        reloadData={reloadPageData}
                        pages={editorPages}
                    />
                </SMCDisabledProvider>
            </div>
        </SMCPage>
            <VerifyButtonsContainer
                disabled={!isPageDataLoaded || !modifyPermission}
                onClickSaveButton={saveButtonOnClick}
                onClickCancelButton={revertButtonOnClick}
            />
        </>
    );
}

const mapStateToProps = (state) => ({
    customImagePermissions: customImagePermissionsSelector(state),
    urlParams: state.router.location.search
});

const mapDispatchToProps = {
    navigateTo: push
}

export default connect(mapStateToProps, mapDispatchToProps)(ImageEditor);